/**
 * Process axis emit helpers for runtime integration.
 * These wrap @aaac/observability's EventCollector for structured process axis events.
 */

import { generateId } from "@aaac/observability";
import type { AttrValue } from "@aaac/observability";
import type { ObserverEmitFn } from "./observability-bridge.js";

export interface ProcessWorkflowOptions {
  workflowId: string;
  sessionId: string;
  parentSpanId?: string;
  /** Optional observer — no-op when undefined. */
  observer?: ObserverEmitFn;
}

export interface ProcessTaskOptions {
  taskId: string;
  targetAgent: string;
  sessionId: string;
  parentSpanId?: string;
  /** Optional observer — no-op when undefined. */
  observer?: ObserverEmitFn;
}

export interface ProcessEditOptions {
  filePath: string;
  artifactId?: string;
  sessionId: string;
  parentSpanId?: string;
  /** Optional observer — no-op when undefined. */
  observer?: ObserverEmitFn;
}

/** Build attribute record; omits keys with undefined/empty-string values. */
function attrs(base: Record<string, AttrValue | undefined>): Record<string, AttrValue> {
  const out: Record<string, AttrValue> = {};
  for (const [k, v] of Object.entries(base)) {
    if (v !== undefined && v !== "") out[k] = v;
  }
  return out;
}

/**
 * Tool names that mutate files on disk and therefore should produce a
 * process.edit event for cross-axis correlation (enrich R3/R5).
 *
 * Read/search/shell tools are intentionally excluded — only file writes count
 * as edits. Matched case-insensitively so adapter-specific casing variants
 * (Write/write, MultiEdit/multi_edit) are all recognised.
 */
const FILE_EDIT_TOOL_NAMES = new Set([
  "write",
  "edit",
  "multiedit",
  "multi_edit",
  "notebookedit",
  "notebook_edit",
]);

/**
 * True when a tool name denotes a file-mutating edit, so a process.edit event
 * should be emitted for it. Used by the task runner to instrument the Process
 * axis from live tool-use progress events (#167).
 */
export function isFileEditTool(toolName: string | undefined): boolean {
  if (!toolName) return false;
  return FILE_EDIT_TOOL_NAMES.has(toolName.toLowerCase());
}

/**
 * Emit process.workflow open span.
 */
export function emitProcessWorkflowOpen(options: ProcessWorkflowOptions): string | undefined {
  if (!options.observer) return undefined;

  const spanId = generateId();
  options.observer({
    source: "aaac-runtime",
    eventType: "process.workflow",
    lifecycle: "open",
    spanId,
    parentSpanId: options.parentSpanId,
    attributes: attrs({
      axis: "process",
      "agent.session_id": options.sessionId,
      "workflow.id": options.workflowId,
    }),
  });
  return spanId;
}

/**
 * Emit process.workflow close span.
 */
export function emitProcessWorkflowClose(
  spanId: string,
  options: { status: string; durationMs?: number; observer?: ObserverEmitFn },
): void {
  if (!options.observer) return;

  options.observer({
    source: "aaac-runtime",
    eventType: "process.workflow",
    lifecycle: "close",
    spanId,
    attributes: attrs({
      axis: "process",
      "workflow.status": options.status,
      ...(options.durationMs !== undefined
        ? { "workflow.duration_ms": options.durationMs }
        : {}),
    }),
  });
}

/**
 * Emit process.task open span.
 */
export function emitProcessTaskOpen(options: ProcessTaskOptions): string | undefined {
  if (!options.observer) return undefined;

  const spanId = generateId();
  options.observer({
    source: "aaac-runtime",
    eventType: "process.task",
    lifecycle: "open",
    spanId,
    parentSpanId: options.parentSpanId,
    attributes: attrs({
      axis: "process",
      "agent.session_id": options.sessionId,
      "task.id": options.taskId,
      "task.type": "implement",
      "task.target_agent": options.targetAgent,
    }),
  });
  return spanId;
}

/**
 * Emit process.task close span.
 */
export function emitProcessTaskClose(
  spanId: string,
  options: { status: string; durationMs?: number; observer?: ObserverEmitFn },
): void {
  if (!options.observer) return;

  options.observer({
    source: "aaac-runtime",
    eventType: "process.task",
    lifecycle: "close",
    spanId,
    attributes: attrs({
      axis: "process",
      "task.status": options.status,
      ...(options.durationMs !== undefined
        ? { "task.duration_ms": options.durationMs }
        : {}),
    }),
  });
}

/**
 * Emit process.edit instant span.
 */
export function emitProcessEdit(options: ProcessEditOptions): string | undefined {
  if (!options.observer) return undefined;

  const spanId = generateId();
  options.observer({
    source: "aaac-runtime",
    eventType: "process.edit",
    lifecycle: "instant",
    spanId,
    parentSpanId: options.parentSpanId,
    attributes: attrs({
      axis: "process",
      "agent.session_id": options.sessionId,
      "edit.path": options.filePath,
      "edit.artifact_id": options.artifactId,
    }),
  });
  return spanId;
}
